import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { Mail, Phone } from 'lucide-react'
import { useLoginMutation, useLoginVerifyMutation, useLazyGetProfileQuery, useSendOtpMutation } from '../../store/api/apiSlice'
import { useAppDispatch } from '../../hooks/useAppStore'
import { setCredentials } from '../../store/slices/authSlice'
import AuthContainer from '../../components/AuthContainer'
import Header from '../../components/Header'
import { parseErrorMessage } from '../../utils/parseErrorMessage'

type LoginStep = 'identifier' | 'otp'
type LoginMethod = 'email' | 'phone'

export default function Login() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [login, { isLoading: isRequestingOtp }] = useLoginMutation()
  const [loginVerify, { isLoading: isVerifying }] = useLoginVerifyMutation()
  const [getProfile, { isLoading: isLoadingProfile }] = useLazyGetProfileQuery()
  const [sendOtp] = useSendOtpMutation()

  const [step, setStep] = useState<LoginStep>('identifier')
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  // Nigeria-only for now.
  const countryCode = '+234'
  const [maskedPhone, setMaskedPhone] = useState('')
  const [maskedEmail, setMaskedEmail] = useState('')
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [errorMessage, setErrorMessage] = useState('')
  const [loadingMessage, setLoadingMessage] = useState('')
  const [resendTimer, setResendTimer] = useState(0)

  // Get the full phone number with country code
  const getFullPhone = () => {
    const num = phoneNumber.trim()
    if (num.startsWith('+')) return num
    return `${countryCode}${num.replace(/^0+/, '')}`
  }

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer <= 0) return
    const timerId = setInterval(() => setResendTimer((t) => t - 1), 1000)
    return () => clearInterval(timerId)
  }, [resendTimer])

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    if (loginMethod === 'email' && !email.trim()) {
      setErrorMessage('Please enter your email address')
      return
    }
    if (loginMethod === 'phone' && !phoneNumber.trim()) {
      setErrorMessage('Please enter your phone number')
      return
    }

    const body = loginMethod === 'phone'
      ? { phoneNumber: getFullPhone() }
      : { email: email.trim() }

    const result = await login(body)

    if ('data' in result && result.data) {
      const data = result.data
      setMaskedPhone(data.phoneNumber || '')
      setMaskedEmail(data.email || '')
      setStep('otp')
      setResendTimer(30)
    } else if ('error' in result) {
      const err = result.error as any
      setErrorMessage(parseErrorMessage(err, 'Login failed. Please try again.'))
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    const otp = code.join('')
    if (otp.length !== 6) {
      setErrorMessage('Please enter the 6-digit code')
      return
    }

    const body = loginMethod === 'phone'
      ? { phoneNumber: getFullPhone(), otp }
      : { email: email.trim(), otp }

    const result = await loginVerify(body)

    if ('data' in result && result.data) {
      const responseData = result.data

      // Store tokens
      localStorage.setItem('access_token', responseData.access_token)
      if (responseData.refresh_token) {
        localStorage.setItem('refresh_token', responseData.refresh_token)
      }

      // Fetch profile and set credentials
      setLoadingMessage('Loading your profile...')
      try {
        const profileResult = await getProfile(undefined)
        if ('data' in profileResult && profileResult.data) {
          dispatch(setCredentials({
            user: profileResult.data,
            token: responseData.access_token
          }))
        } else {
          dispatch(setCredentials({
            user: responseData.user,
            token: responseData.access_token
          }))
        }
      } catch {
        dispatch(setCredentials({
          user: responseData.user,
          token: responseData.access_token
        }))
      }

      navigate('/dashboard', { replace: true })
    } else if ('error' in result) {
      const err = result.error as any
      setErrorMessage(parseErrorMessage(err, 'Invalid or expired OTP'))
    }
  }

  const handleResendOtp = async () => {
    if (resendTimer > 0) return
    setErrorMessage('')

    if (loginMethod === 'phone') {
      try {
        await sendOtp({ phoneNumber: getFullPhone() }).unwrap()
        setResendTimer(30)
      } catch (err) {
        setErrorMessage(parseErrorMessage(err, 'Could not resend OTP'))
      }
    } else {
      const result = await login({ email: email.trim() })
      if ('data' in result && result.data) {
        setResendTimer(30)
      } else if ('error' in result) {
        setErrorMessage(parseErrorMessage(result.error, 'Could not resend OTP'))
      }
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...code]
      newCode[index] = value
      setCode(newCode)
      if (value && index < 5) {
        document.getElementById(`login-code-${index + 1}`)?.focus()
      }
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const digits = pasted.split('')
    setCode((prev) => prev.map((_d, idx) => digits[idx] || ''))
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`login-code-${index - 1}`)?.focus()
    }
  }

  // Clear error when user types
  useEffect(() => {
    if (errorMessage) setErrorMessage('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, phoneNumber, code])

  const isLoading = isRequestingOtp || isVerifying || isLoadingProfile

  return (
    <div className='min-h-screen bg-[#FFFFFF] flex flex-col'>
      <Header />
      <AuthContainer>
        <div className='w-full'>
          {/* Header */}
          <div className='mb-8'>
            <h1 className='text-gray-900 text-[32px] text-start md:text-[40px] font-bold mb-3'>
              Log In
            </h1>
            <p className='text-gray-500 text-[14px] text-start md:text-[15px]'>
              Welcome Back!
            </p>
            <p className='text-gray-600 text-[14px] text-start md:text-[15px]'>
              {step === 'identifier'
                ? 'Log in with your email or phone number'
                : `Enter the 6-digit code sent to ${maskedPhone || maskedEmail || 'your phone/email'}. Check your email or WhatsApp for the code.`}
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-lg'>
              <p className='text-red-600 text-sm'>{errorMessage}</p>
            </div>
          )}

          {step === 'identifier' ? (
            /* Step 1: Email/Phone Input with Tabs */
            <form onSubmit={handleRequestOtp} className='space-y-6'>
              {/* Tabs */}
              <div className='flex border-b border-gray-200'>
                <button
                  type='button'
                  onClick={() => { setLoginMethod('email'); setErrorMessage('') }}
                  className={`flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium border-b-2 transition-colors ${
                    loginMethod === 'email'
                      ? 'border-black text-gray-900'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <Mail className='w-4 h-4' />
                  Email
                </button>
                <button
                  type='button'
                  onClick={() => { setLoginMethod('phone'); setErrorMessage('') }}
                  className={`flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium border-b-2 transition-colors ${
                    loginMethod === 'phone'
                      ? 'border-black text-gray-900'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <Phone className='w-4 h-4' />
                  Phone Number
                </button>
              </div>

              {/* Email Input */}
              {loginMethod === 'email' && (
                <div>
                  <label htmlFor='email' className='block text-gray-900 text-start text-[14px] font-medium mb-2'>
                    Email Address
                  </label>
                  <div className='relative'>
                    <Mail className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
                    <input
                      type='email'
                      id='email'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder='jane.doe@example.com'
                      className='w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-[14px] placeholder-gray-400 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900'
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
              )}

              {/* Phone Input */}
              {loginMethod === 'phone' && (
                <div>
                  <label htmlFor='phoneNumber' className='block text-gray-900 text-start text-[14px] font-medium mb-2'>
                    Phone Number
                  </label>
                  <div className='flex gap-2'>
                    <div className='flex items-center gap-1.5 w-24 px-3 py-3 bg-gray-50 border border-gray-300 rounded-lg text-[14px] text-gray-900'>
                      <span className='text-base leading-none'>🇳🇬</span>
                      <span>+234</span>
                    </div>
                    <div className='relative flex-1'>
                      <Phone className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
                      <input
                        type='tel'
                        id='phoneNumber'
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder='8012345678'
                        className='w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-[14px] placeholder-gray-400 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900'
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>
              )}

              <button
                type='submit'
                disabled={isLoading}
                className='w-full bg-black text-white py-3 cursor-pointer rounded-lg text-[15px] font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {isRequestingOtp ? 'Sending OTP...' : 'Continue'}
              </button>

              <div className='text-center'>
                <span className='text-gray-600 text-[14px]'>
                  Don't have an account?{' '}
                  <button
                    type='button'
                    onClick={() => navigate('/signup')}
                    className='text-orange-500 hover:underline font-medium bg-transparent border-0 cursor-pointer'
                  >
                    Sign Up
                  </button>
                </span>
              </div>
            </form>
          ) : (
            /* Step 2: OTP Verification */
            <form onSubmit={handleVerifyOtp} className='space-y-6'>
              <div className='flex gap-3 justify-center md:justify-start'>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    id={`login-code-${index}`}
                    type='text'
                    inputMode='numeric'
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    className='w-12 h-12 md:w-14 md:h-14 text-center text-xl font-semibold bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent'
                    disabled={isLoading}
                  />
                ))}
              </div>

              <button
                type='submit'
                disabled={isLoading}
                className='w-full bg-black text-white py-3 cursor-pointer rounded-lg text-[15px] font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {isVerifying ? 'Verifying...' : isLoadingProfile ? loadingMessage : 'Verify & Log In'}
              </button>

              <div className='text-center'>
                <p className='text-gray-600 text-[14px]'>
                  Didn't get the code?{' '}
                  {resendTimer > 0 ? (
                    <span className='text-orange-500 font-medium'>
                      Resend OTP in 00:{String(resendTimer).padStart(2, '0')}
                    </span>
                  ) : (
                    <button
                      type='button'
                      onClick={handleResendOtp}
                      className='text-orange-500 hover:underline font-medium bg-transparent border-0 cursor-pointer'
                    >
                      Resend OTP
                    </button>
                  )}
                </p>
              </div>

              <div className='text-center'>
                <button
                  type='button'
                  onClick={() => {
                    setStep('identifier')
                    setCode(['', '', '', '', '', ''])
                    setErrorMessage('')
                  }}
                  className='text-gray-500 hover:underline text-[14px] bg-transparent border-0 cursor-pointer'
                >
                  Use a different email or phone
                </button>
              </div>
            </form>
          )}
        </div>
      </AuthContainer>
    </div>
  )
}
